import React, { useCallback } from 'react';
import noop from 'lodash/noop';
import { AsyncBulkOpsCore } from '@atlassian/jira-bulk-operations-async-core';
import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/types.tsx';
import type { ReferenceLoader } from '@atlassian/jira-bulk-operations-common/src/utils/reference-loader/use-reference-loader/index.tsx';
import { useBulkSelectionStore } from '@atlassian/jira-business-list/src/controllers/bulk-selection/bulk-selection-store/index.tsx';
import { useBulkEditCacheActions } from '../../../../controllers/bulk-selection/bulk-edit/optimistic-edit';
import { useGroupBy } from '../../../../controllers/group-by';
import { useUpdateGroupIssue } from '../../../../controllers/update-group-issue';

type BulkOperationsLoaderProps = {
	bulkOperationsForm: ReferenceLoader;
	onOpenSidebar: () => void;
	onCloseSidebar: () => void;
};

export const BulkOperationsLoader = ({
	bulkOperationsForm,
	onOpenSidebar,
	onCloseSidebar,
}: BulkOperationsLoaderProps) => {
	const [groupByFieldId] = useGroupBy();
	const { bulkOptimisticUpdate } = useUpdateGroupIssue();
	const { updateSelectedItemsInCache } = useBulkEditCacheActions();
	const [{ bulkEditableIssues: selectedIssues }] = useBulkSelectionStore();

	const onUpdateStart = useCallback(
		(res: IssuesUpdatePayload[]) => {
			const { data, issueIds } = res[0];
			if (data) {
				if (groupByFieldId) {
					// TODO: Transform data to ListItemField type for bulkOptimisticUpdate
					bulkOptimisticUpdate(issueIds, []);
				} else {
					updateSelectedItemsInCache({ data, issueIds });
				}
			}
		},
		[groupByFieldId, bulkOptimisticUpdate, updateSelectedItemsInCache],
	);

	// TODO remove this action on bulk-transition-multiple-workflows_wtnvv cleanup
	const onUpdateStartOld = useCallback(
		({ data, issueIds }: IssuesUpdatePayload) => {
			if (data) {
				if (groupByFieldId) {
					// TODO: Transform data to ListItemField type for bulkOptimisticUpdate
					bulkOptimisticUpdate(issueIds, []);
				} else {
					updateSelectedItemsInCache({ data, issueIds });
				}
			}
		},
		[groupByFieldId, bulkOptimisticUpdate, updateSelectedItemsInCache],
	);

	const onUpdateError = useCallback(noop, []);

	const onUpdateSuccess = useCallback(noop, []);

	const onOpenForm = useCallback(() => {
		onOpenSidebar();
	}, [onOpenSidebar]);

	const onCloseForm = useCallback(() => {
		onCloseSidebar();
	}, [onCloseSidebar]);

	return (
		<AsyncBulkOpsCore
			bulkOperationsForm={bulkOperationsForm}
			onOpenForm={onOpenForm}
			onCloseForm={onCloseForm}
			onUpdateStart={onUpdateStart}
			onUpdateStartOld={onUpdateStartOld} // TODO remove this action on bulk-transition-multiple-workflows_wtnvv cleanup
			onUpdateError={onUpdateError}
			onUpdateSuccess={onUpdateSuccess}
			onLegacyBulkEditRequest={noop}
			onSelectAll={noop}
			selectableIssueIds={[]}
			selectedIssueIds={Array.from(selectedIssues).map(Number) ?? []}
			extraAnalyticsAttributes={{}}
			onClearAll={noop}
			onClearAllVisible={noop}
			// configure when resolving the ticket https://jplat.jira.atlassian.cloud/browse/THOR-3771. leaving this switched on by default for now.
			hasBulkEditPermission
		/>
	);
};
